import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { HandoffRequest } from './HandoffRequest';

import type { Language } from 'config/enums';
import { Gender } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { callLanguageCodeSelector } from 'features/call/call-base/store/selectors';
import { usePopoverContext } from 'features/menu/context';
import { handleError } from 'features/notification/store';
import { TeamingStatus } from 'features/teaming/teaming-base/enums';
import {
  createTeamingOptions,
  requestHandoffAnalytics,
} from 'features/teaming/teaming-base/helpers';
import {
  requestRemoteTeaming,
  setTeamingStatus,
} from 'features/teaming/teaming-base/store';
import { voiceSessionIdSelector } from 'features/voice-session/store';
import { callerSessionAnalyticsInfo } from 'features/caller-session/helpers';
import { sendAnalytics } from 'features/analytics/store';
import { LogLevel } from 'features/analytics/enums';
import { useVoiceMeetingSessionConnect } from 'features/voice-meeting/hooks';
import { isZoomConferenceSelector } from 'features/call/call-conference/store';

const DEFAULT_GENDER = Gender.U;
const DEFAULT_LOADING_STATE = false;

/**
 * Container component for handling warm handoff requests between agents.
 * Manages the state and logic for requesting handoffs, including gender and language preferences.
 */
export const HandoffRequestContainer: FC = () => {
  const defaultLanguage = useAppSelector(callLanguageCodeSelector);
  const [isLoading, setIsLoading] = useState(DEFAULT_LOADING_STATE);
  const [gender, setGender] = useState(DEFAULT_GENDER);
  const [language, setLanguage] = useState(defaultLanguage as Language);
  const dispatch = useAppDispatch();
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);
  const { handleClosePopover } = usePopoverContext();
  const { startNewVoiceSessionOnlyAgent } = useVoiceMeetingSessionConnect();
  const isZoomConference = useAppSelector(isZoomConferenceSelector);

  const teamingOptions = useCallback(
    () => createTeamingOptions(gender, language),
    [gender, language]
  );

  const handleRequestHandoff = useCallback(async () => {
    setIsLoading(true);
    dispatch(callerSessionAnalyticsInfo('Handed off call in Caller Session'));
    try {
      dispatch(requestHandoffAnalytics('handleRequestHandoff'));
      dispatch(setTeamingStatus(TeamingStatus.REQUESTED));

      if (!voiceSessionId && !isZoomConference) {
        dispatch(
          sendAnalytics({
            Method: 'handleRequestHandoff',
            Level: LogLevel.INFO,
            Message: 'Adding voice session for handoff request',
          })
        );
        await startNewVoiceSessionOnlyAgent();
      }
      await dispatch(requestRemoteTeaming(teamingOptions())).unwrap();
    } catch (error) {
      dispatch(setTeamingStatus(TeamingStatus.NONE));
      dispatch(
        handleError({
          error,
          methodName: 'handleRequestHandoff',
        })
      );
    } finally {
      setIsLoading(false);
      handleClosePopover();
    }
  }, [
    dispatch,
    voiceSessionId,
    teamingOptions,
    startNewVoiceSessionOnlyAgent,
    handleClosePopover,
    isZoomConference,
  ]);

  const handoffProps = useMemo(
    () => ({
      gender,
      language,
      onGenderChange: setGender,
      onLanguageChange: setLanguage,
      isLoading,
      onRequestHandoff: handleRequestHandoff,
      onClose: handleClosePopover,
    }),
    [gender, language, isLoading, handleRequestHandoff, handleClosePopover]
  );

  return <HandoffRequest {...handoffProps} />;
};
